/**
 * Upload React Query Hooks
 *
 * Provides hooks for upload operations including:
 * - Upload job lifecycle (create, complete, cancel)
 * - File uploads with progress tracking
 * - Real-time SSE progress updates
 * - Dataset management
 *
 * @module hooks/useUpload
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import { uploadApi, uploadKeys } from '@/lib/api/upload';
import type {
  CreateUploadJobRequest,
  CreateUploadJobResponse,
  CompleteUploadJobRequest,
  CompleteUploadJobResponse,
  UploadJob,
  Dataset,
  CreateDatasetRequest,
  LocalFile,
  FileUploadProgress,
  UploadProgressEvent,
} from '@/features/upload/types/upload.types';

// ============================================================================
// Upload Job Hooks
// ============================================================================

/**
 * Hook to create a new upload job
 *
 * Creates a new upload job and optionally receives pre-signed URLs for uploads.
 *
 * @param options - Optional mutation options
 * @returns Mutation for creating upload jobs
 *
 * @example
 * ```typescript
 * const createJob = useCreateUploadJob();
 *
 * const handleCreate = async () => {
 *   const result = await createJob.mutateAsync({
 *     dataset_name: 'my-dataset',
 *     total_files: 5,
 *     validate_schema: true
 *   });
 *   console.log('Job created:', result.upload_job.id);
 * };
 * ```
 */
export function useCreateUploadJob(
  options?: UseMutationOptions<CreateUploadJobResponse, Error, CreateUploadJobRequest>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUploadJobRequest) => uploadApi.createUploadJob(data),
    onSuccess: () => {
      // Invalidate job list queries
      queryClient.invalidateQueries({ queryKey: uploadKeys.jobs() });
    },
    ...options,
  });
}

/**
 * Hook to get upload job details
 *
 * @param uploadJobId - Upload job ID
 * @param options - Optional query options
 * @returns Query for upload job details
 *
 * @example
 * ```typescript
 * const { data: job, isLoading } = useUploadJob('job-123');
 * ```
 */
export function useUploadJob(
  uploadJobId: string,
  options?: UseQueryOptions<UploadJob, Error>
) {
  return useQuery({
    queryKey: uploadKeys.job(uploadJobId),
    queryFn: () => uploadApi.getUploadJob(uploadJobId),
    enabled: !!uploadJobId && options?.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
    ...options,
  });
}

/**
 * Hook to complete an upload job
 *
 * Marks the upload job as complete and triggers final processing.
 *
 * @param options - Optional mutation options
 * @returns Mutation for completing upload jobs
 *
 * @example
 * ```typescript
 * const completeJob = useCompleteUploadJob();
 *
 * const handleComplete = async () => {
 *   await completeJob.mutateAsync({
 *     uploadJobId: 'job-123',
 *     data: { status: 'completed' }
 *   });
 * };
 * ```
 */
export function useCompleteUploadJob(
  options?: UseMutationOptions<
    CompleteUploadJobResponse,
    Error,
    { uploadJobId: string; data: CompleteUploadJobRequest }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uploadJobId, data }: { uploadJobId: string; data: CompleteUploadJobRequest }) =>
      uploadApi.completeUploadJob(uploadJobId, data),
    onSuccess: (data, variables) => {
      // Update job query cache
      queryClient.setQueryData(uploadKeys.job(variables.uploadJobId), data.upload_job);
      // Invalidate job list
      queryClient.invalidateQueries({ queryKey: uploadKeys.jobs() });
    },
    ...options,
  });
}

/**
 * Hook to cancel an upload job
 *
 * Cancels an in-progress upload job and cleans up uploaded files.
 *
 * @param options - Optional mutation options
 * @returns Mutation for cancelling upload jobs
 *
 * @example
 * ```typescript
 * const cancelJob = useCancelUploadJob();
 *
 * const handleCancel = async () => {
 *   await cancelJob.mutateAsync('job-123');
 * };
 * ```
 */
export function useCancelUploadJob(
  options?: UseMutationOptions<{ upload_job: UploadJob }, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uploadJobId: string) => uploadApi.cancelUploadJob(uploadJobId),
    onSuccess: (data, uploadJobId) => {
      // Update job query cache
      queryClient.setQueryData(uploadKeys.job(uploadJobId), data.upload_job);
      // Invalidate job list
      queryClient.invalidateQueries({ queryKey: uploadKeys.jobs() });
    },
    ...options,
  });
}

// ============================================================================
// Dataset Hooks
// ============================================================================

/**
 * Hook to list datasets
 *
 * @param params - Optional query parameters
 * @param options - Optional query options
 * @returns Query for datasets list
 *
 * @example
 * ```typescript
 * const { data } = useDatasets({ search: 'sales', limit: 50 });
 * console.log(data?.datasets);
 * ```
 */
export function useDatasets(
  params?: { search?: string; limit?: number; offset?: number },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: uploadKeys.datasets(params),
    queryFn: () => uploadApi.listDatasets(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled,
  });
}

/**
 * Hook to create a new dataset
 *
 * @param options - Optional mutation options
 * @returns Mutation for creating datasets
 *
 * @example
 * ```typescript
 * const createDataset = useCreateDataset();
 *
 * const handleCreate = async () => {
 *   const result = await createDataset.mutateAsync({
 *     name: 'new-dataset',
 *     description: 'My new dataset'
 *   });
 * };
 * ```
 */
export function useCreateDataset(
  options?: UseMutationOptions<{ dataset: Dataset }, Error, CreateDatasetRequest>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDatasetRequest) => uploadApi.createDataset(data),
    onSuccess: () => {
      // Invalidate datasets queries
      queryClient.invalidateQueries({ queryKey: uploadKeys.datasets() });
    },
    ...options,
  });
}

/**
 * Hook to validate dataset name
 *
 * @param name - Dataset name to validate
 * @param options - Optional query options
 * @returns Query for validation result
 *
 * @example
 * ```typescript
 * const { data: validation } = useValidateDatasetName('my-dataset');
 * if (!validation?.valid) {
 *   console.error(validation?.message);
 * }
 * ```
 */
export function useValidateDatasetName(
  name: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: uploadKeys.validateName(name),
    queryFn: () => uploadApi.validateDatasetName(name),
    enabled: !!name && name.length > 0 && options?.enabled !== false,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to get dataset by name
 *
 * @param name - Dataset name
 * @param options - Optional query options
 * @returns Query for dataset details
 *
 * @example
 * ```typescript
 * const { data: dataset } = useDatasetByName('sales-data');
 * ```
 */
export function useDatasetByName(
  name: string,
  options?: UseQueryOptions<Dataset | null, Error>
) {
  return useQuery({
    queryKey: uploadKeys.datasetByName(name),
    queryFn: () => uploadApi.getDatasetByName(name),
    enabled: !!name && options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

// ============================================================================
// File Upload Hook
// ============================================================================

/**
 * Hook for uploading files with progress tracking
 *
 * Provides functions to upload single or multiple files with progress tracking.
 * Maintains a progress state map for all files being uploaded.
 *
 * @returns Object with upload functions and progress state
 *
 * @example
 * ```typescript
 * const {
 *   uploadFile,
 *   uploadMultiple,
 *   uploadProgress,
 *   getFileProgress,
 *   clearProgress,
 *   isUploading
 * } = useFileUpload();
 *
 * // Upload multiple files
 * const results = await uploadMultiple('job-123', files);
 * ```
 */
export function useFileUpload() {
  const [uploadProgress, setUploadProgress] = useState<Record<string, FileUploadProgress>>({});
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Update progress for a specific file
   */
  const updateFileProgress = useCallback(
    (fileId: string, update: Partial<FileUploadProgress>) => {
      setUploadProgress((prev) => ({
        ...prev,
        [fileId]: {
          ...prev[fileId],
          fileId,
          ...update,
        },
      }));
    },
    []
  );

  /**
   * Get progress for a specific file
   */
  const getFileProgress = useCallback(
    (fileId: string): FileUploadProgress | undefined => {
      return uploadProgress[fileId];
    },
    [uploadProgress]
  );

  /**
   * Clear all progress
   */
  const clearProgress = useCallback(() => {
    setUploadProgress({});
    setIsUploading(false);
  }, []);

  /**
   * Upload single file mutation
   */
  const uploadFileMutation = useMutation({
    mutationFn: async ({
      uploadJobId,
      file,
    }: {
      uploadJobId: string;
      file: LocalFile;
    }) => {
      // Initialize progress
      updateFileProgress(file.id, {
        fileId: file.id,
        progress: 0,
        status: 'uploading',
      });

      // Upload with progress callback
      const result = await uploadApi.uploadFile(
        uploadJobId,
        file,
        (progress) => {
          updateFileProgress(file.id, {
            fileId: file.id,
            progress,
            status: 'uploading',
          });
        }
      );

      return { file, result };
    },
    onSuccess: ({ file }) => {
      updateFileProgress(file.id, {
        fileId: file.id,
        progress: 100,
        status: 'completed',
      });
    },
    onError: (error: Error, { file }) => {
      updateFileProgress(file.id, {
        fileId: file.id,
        status: 'failed',
        error: error.message,
      });
    },
  });

  /**
   * Upload single file (async)
   */
  const uploadFileAsync = useCallback(
    async (uploadJobId: string, file: LocalFile) => {
      return uploadFileMutation.mutateAsync({ uploadJobId, file });
    },
    [uploadFileMutation]
  );

  /**
   * Upload multiple files sequentially
   */
  const uploadMultiple = useCallback(
    async (uploadJobId: string, files: LocalFile[]) => {
      setIsUploading(true);

      const results: Array<{
        file: LocalFile;
        success: boolean;
        error?: string;
      }> = [];

      for (const file of files) {
        try {
          await uploadFileAsync(uploadJobId, file);
          results.push({ file, success: true });
        } catch (error: any) {
          results.push({
            file,
            success: false,
            error: error.message || 'Upload failed',
          });
        }
      }

      setIsUploading(false);
      return results;
    },
    [uploadFileAsync]
  );

  return {
    /**
     * Upload single file (mutation)
     */
    uploadFile: uploadFileMutation.mutate,

    /**
     * Upload single file (async)
     */
    uploadFileAsync,

    /**
     * Upload multiple files sequentially
     */
    uploadMultiple,

    /**
     * Progress map for all files
     */
    uploadProgress,

    /**
     * Get progress for specific file
     */
    getFileProgress,

    /**
     * Clear all progress
     */
    clearProgress,

    /**
     * Whether any upload is in progress
     */
    isUploading,
  };
}

// ============================================================================
// Upload Progress SSE Hook
// ============================================================================

/**
 * Options for useUploadProgress hook
 */
export interface UseUploadProgressOptions {
  /**
   * Callback when progress event is received
   */
  onProgress?: (event: UploadProgressEvent) => void;

  /**
   * Callback when upload completes
   */
  onComplete?: (event: UploadProgressEvent) => void;

  /**
   * Callback when error occurs
   */
  onError?: (error: Error) => void;

  /**
   * Whether to automatically connect
   * @default true
   */
  autoConnect?: boolean;

  /**
   * Reconnect on error
   * @default true
   */
  reconnect?: boolean;

  /**
   * Maximum reconnect attempts
   * @default 5
   */
  maxReconnectAttempts?: number;

  /**
   * Reconnect delay in ms
   * @default 1000
   */
  reconnectDelay?: number;
}

/**
 * Hook for real-time upload progress via Server-Sent Events (SSE)
 *
 * Connects to an SSE stream for real-time progress updates during file uploads.
 * Provides automatic reconnection on errors.
 *
 * @param uploadJobId - Upload job ID to track
 * @param options - Optional configuration
 * @returns Object with connection state and progress data
 *
 * @example
 * ```typescript
 * const {
 *   isConnected,
 *   progress,
 *   error,
 *   connect,
 *   disconnect
 * } = useUploadProgress('job-123', {
 *   onProgress: (event) => console.log('Progress:', event.progress_percentage),
 *   onComplete: (event) => console.log('Upload complete!'),
 *   onError: (error) => console.error('SSE error:', error)
 * });
 * ```
 */
export function useUploadProgress(
  uploadJobId: string | null,
  options: UseUploadProgressOptions = {}
) {
  const {
    onProgress,
    onComplete,
    onError,
    autoConnect = true,
    reconnect = true,
    maxReconnectAttempts = 5,
    reconnectDelay = 1000,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [progress, setProgress] = useState<UploadProgressEvent | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * Disconnect from SSE stream
   */
  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    setIsConnected(false);
  }, []);

  // Holds the latest `connect` for the self-scheduled reconnect retry below.
  // The retry closure reads this ref (a mutable container) instead of
  // capturing `connect` directly, since a useCallback referencing itself
  // before its own declaration finishes is an immutability hazard.
  const connectRef = useRef<() => void>(() => undefined);

  /**
   * Connect to SSE stream
   */
  const connect = useCallback(() => {
    if (!uploadJobId) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const url = uploadApi.getProgressStreamUrl(uploadJobId);
      const eventSource = new EventSource(url);

      eventSource.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      eventSource.onmessage = (event) => {
        try {
          const data: UploadProgressEvent = JSON.parse(event.data);
          setProgress(data);

          // Call progress callback
          onProgress?.(data);

          // Call complete callback if job is complete
          if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
            onComplete?.(data);
            disconnect();
          }
        } catch (err) {
          console.error('Failed to parse SSE message:', err);
        }
      };

      eventSource.onerror = () => {
        setIsConnected(false);
        const error = new Error('SSE connection error');
        setError(error);
        onError?.(error);

        eventSource.close();

        // Attempt reconnection
        if (reconnect && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            connectRef.current();
          }, reconnectDelay * reconnectAttemptsRef.current);
        }
      };

      eventSourceRef.current = eventSource;
    } catch (err: any) {
      setError(err);
      onError?.(err);
    }
  }, [uploadJobId, onProgress, onComplete, onError, reconnect, maxReconnectAttempts, reconnectDelay, disconnect]);

  // Keep the ref in sync outside of render.
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect && uploadJobId) {
      connectRef.current();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, uploadJobId, connect, disconnect]);

  return {
    /**
     * Whether SSE connection is active
     */
    isConnected,

    /**
     * Latest progress event
     */
    progress,

    /**
     * Connection error
     */
    error,

    /**
     * Manually connect to SSE
     */
    connect,

    /**
     * Manually disconnect from SSE
     */
    disconnect,
  };
}
